import { Grade, LoginClassType } from "types/common/common.types";
import { Salutation } from "types/common/common.types";
import { UserType } from "types/common/common.types";
import {
  Control,
  UseFormHandleSubmit,
  SubmitHandler,
  UseFormGetValues,
  FieldValues
} from "react-hook-form";
import { Dispatch, SetStateAction } from "react";
import { IPackageAndTenureSummaryDetails } from "services/api/packages/packageTenureSummaryDetails.types";
import { IClassOptions } from "constants/constants";
import { GeoLocationData } from "hooks/use-geolocation/use-geolocation";

export type IUseStudent<T extends FieldValues> = {
  isValid: boolean;
  control: Control<T>;
  handleSubmit: UseFormHandleSubmit<T>;
  onSubmit: SubmitHandler<T>;
  getPhoneNumberFromRegisterToken: string;
  isLoading: boolean;
};

export type IUseParent<T extends FieldValues> = {
  isValid: boolean;
  control: Control<T>;
  handleSubmit: UseFormHandleSubmit<T>;
  handleCheckbox: () => void;
  onSubmit: SubmitHandler<T>;
  getPhoneNumberFromRegisterToken: string;
  isLoading: boolean;
};

export type IUseLoginStudent<T extends FieldValues> = {
  isValid: boolean;
  control: Control<T>;
  getValues: UseFormGetValues<
    Omit<StudentFormSchema, "parentName" | "parentNumber">
  >;
  handleSubmit: UseFormHandleSubmit<T>;
  onSubmit: SubmitHandler<T>;
  isLoading: boolean;
  isSuccessModalOpen: boolean;
  setIsSuccessModalOpen: Dispatch<SetStateAction<boolean>>;
};

export type IUseLoginParent<T extends FieldValues> = {
  isValid: boolean;
  control: Control<T>;
  getValues: UseFormGetValues<ParentFormSchema>;
  handleSubmit: UseFormHandleSubmit<T>;
  onSubmit: SubmitHandler<T>;
  isSuccessModalOpen: boolean;
  setIsSuccessModalOpen: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
};

export type IUserProfileFormProps = {
  userType: UserType;
  packageId?: string;
  tenureId?: string;
  gradeFromPackageData?: string;
  summaryPackageData?: IPackageAndTenureSummaryDetails;
};

export type IUserProfileForm = {
  userType: UserType;
  defaultGrade?: string;
};

export interface IUserProfileFormWithMixpanel extends IUserProfileForm {
  summaryPackageData: IPackageAndTenureSummaryDetails | undefined;
}

export type StudentFormSchema = {
  name: string;
  userType: UserType;
  grade: Grade | string;
  classType: LoginClassType;
  email: string;
  parentName: string;
  salutation: Salutation;
  parentNumber: string;
};

export type ParentFormSchema = {
  userType: UserType;
  salutation: Salutation;
  fullName: string;
  grade: Grade | string;
  classType: LoginClassType;
  email: string;
  studentName: string;
  studentNumber: string;
  isParent: boolean;
};

export type IUseClassOptionsChange = {
  gradeOptions: IClassOptions[];
  handleClassOptionsChange: () => void;
};

export type OptionType = {
  name: string;
  value: string;
};

export type IUseUpdateUserProfileForm<T extends FieldValues> = {
  control: Control<T>;
  cityList: OptionType[];
  schoolList: OptionType[];
  fetchCityList: () => void;
  fetchSchoolList: () => void;
  isValid: boolean;
  handleSubmit: UseFormHandleSubmit<T>;
  onSubmit: SubmitHandler<T>;
  isSuccessModalOpen: boolean;
  setIsSuccessModalOpen: Dispatch<SetStateAction<boolean>>;
  getGPSData: () => void;
  geoLocationData: GeoLocationData;
  locationSource: UserCitySource | null;
  setLocationSource: (locationSource: UserCitySource) => void;
  isLoading: boolean;
};

export type UpdateUserFormSchema = {
  parentNumber: string;
  city: string;
  school: string;
};

export enum UserCitySource {
  Manual = "manual",
  GPS = "GPS",
  DropDown = "dropdown"
}
